import React, {useRef} from 'react'
import styled, {StyleSheetManager} from 'styled-components'
import rtlPlugin from 'stylis-plugin-rtl'
import Head from 'next/head'
import Script from 'next/script'
import {GetStaticPaths, GetStaticProps} from 'next'
import {useRouter} from 'next/router'
import type {Entry} from 'contentful'

import Header, {IHeader, SimplifiedHeader} from '@miro-site/components_header'
import Footer, {IFooter} from '@miro-site/components_footer'
import PreviewMode from '@miro-site/core/src/components/preview-mode/PreviewMode'
import {theme as acqusitionHeaderTheme} from '@miro-site/components_acquisition-header'

import logger from '../src/logger/logger'
import getMenu from '../src/lib/contentful/getMenu'
import getPageData from '../src/lib/contentful/getPageData'
import getTextTokens from '../src/lib/contentful/getTextTokens'
import getPageBySlug from '../src/lib/contentful/getPageBySlug'
import Components, {getComponentName} from '../src/components'
import {LandingPage, Translation} from '../src/lib/contentful/types'
import {replaceSectionValues, toAbsoluteUrl} from '../src/lib/contentful/helpers'
import {useLocalization} from 'packages/localization/src'
import dynamic from 'next/dynamic'

export const getStaticPaths: GetStaticPaths = async () => {
  let paths = []

  // We are using fallback behaviour for our dev mode to make performance better
  if (process.env.IS_DEVELOPMENT_SERVER) {
    return {
      paths,
      fallback: 'blocking',
    }
  }

  const pageData = await getPageData()

  paths = Object.keys(pageData).map(slug => {
    return {
      params: {slug: slug.split('/')},
    }
  })

  return {
    paths,
    fallback: 'blocking',
  }
}

export type LandingPageProps = {
  header: IHeader | null
  footer: IFooter | null
  page: Entry<LandingPage>
  translations: Translation[]
  textTokens: Record<string, string>
  preview?: boolean
}

export const getStaticProps: GetStaticProps<LandingPageProps> = async context => {
  const slug =
    (Array.isArray(context.params.slug) ? context.params.slug.join('/') : context.params.slug) || ''

  const preview = !!context.preview

  const log = logger.child({slug: slug, preview})

  const revalidate = process.env.NEXT_REVALIDATE_TIMEOUT

  log.info('Starting regeneration...')

  const textTokens = await getTextTokens()
  const pageData = await getPageBySlug(slug, preview)

  const {entry: page} = pageData || {}

  if (!page) {
    log.info('Page not found')

    return {
      notFound: true,
      revalidate: revalidate ? Number(revalidate) : false,
    }
  }

  log.info(`Found page with locale: ${page.sys.locale}`)

  let header: LandingPageProps['header'] = null
  let footer: LandingPageProps['footer'] = null

  if (!page.fields.acquisitionPage) {
    log.info('Fetching menu data...')

    const menu = await getMenu(page.sys.locale, preview)

    header = menu[0] || null
    footer = menu[1] || null
  }

  log.info('Regeneration finished')

  let {translations = []} = pageData || {}

  if (slug.indexOf('homepage') !== -1) {
    const homepageLocales = ['en', 'fr', 'es', 'pt', 'nl', 'de', 'ja', 'it', 'ru', 'ko']

    // Page is part of homepage experiment and we should:
    // 1 - Show all translations
    // 2 - Add static hreflang
    translations = []

    homepageLocales.forEach(locale => {
      translations.push({
        locale,
        isDefault: locale === 'en' ? true : false,
        url: locale === 'en' ? '' : locale,
      })
    })
  }

  return {
    props: {
      header,
      footer,
      page,
      translations,
      preview,
      textTokens,
    },
    revalidate: revalidate ? Number(revalidate) : false,
  }
}

const Container = styled.div`
  position: relative;
  min-height: 100%;
  height: auto !important;
`

const HeaderWrapper = styled.div`
  position: sticky;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 200;

  &.mobile-menu-opened {
    position: fixed;
    z-index: 700;
  }
`

/**
 * For experiment RTB-128963 we are showing a popup baneer at the end of the page
 * Based on the experiment result, we will take care of this below code
 */
const DynamicPopupBanner = dynamic(() => import('../components/PopupBanner'))

const Page = (props: LandingPageProps): React.ReactElement => {
  const {header, footer, page, translations, preview, textTokens} = props

  const router = useRouter()

  if (router.isFallback) {
    return <></>
  }

  let headerBackground

  const layout = page.fields.layout.reduce((output, section, i) => {
    if (section.hidden) {
      return output
    }

    const Component = Components[getComponentName(section)]

    if (!Component) {
      return output
    }

    replaceSectionValues(section.values, textTokens)

    output.push(<Component key={section.id} {...section.values} />)

    // Guess the background for Acquisition Header component
    if (
      page.fields.acquisitionPage &&
      i === 0 &&
      typeof section.values.background === 'string' &&
      acqusitionHeaderTheme.background[section.values.background]
    ) {
      headerBackground = section.values.background
    }

    return output
  }, [])

  const headerWrapperRef = useRef<HTMLDivElement>(null)

  /**
   * For simplified nav bar experiment,
   * we are loading this simplified navbar only for this page id
   * Based on the experiment logics we will change the below logics
   */
  const isSimplifiedNavbarPage = page.sys.id === '6R6h895ov29pCriqAZ9j6g'

  const headerComponent = isSimplifiedNavbarPage ? (
    <HeaderWrapper ref={headerWrapperRef}>
      <SimplifiedHeader containerNode={headerWrapperRef.current} {...header} />
    </HeaderWrapper>
  ) : header ? (
    <HeaderWrapper ref={headerWrapperRef}>
      <Header containerNode={headerWrapperRef.current} {...header} />
    </HeaderWrapper>
  ) : (
    <Components.AcquisitionHeader background={headerBackground} />
  )
  const footerComponent = footer ? <Footer {...footer} /> : <Components.AcquisitionFooter />

  const defaultOpenGraphImage = `${
    process.env.NEXT_PUBLIC_ASSET_PREFIX || ''
  }/assets/images/miro.png`

  const {isRTL} = useLocalization()

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <StyleSheetManager stylisPlugins={isRTL && [rtlPlugin]}>
      <>
        <Head>
          <title>{page.fields.title}</title>
          <meta name="description" content={page.fields.description} />

          <link rel="canonical" href={page.fields.canonicalUrl} />

          {translations.map((l, i) => {
            const hreflang = l.isDefault ? 'x-default' : l.locale

            return <link key={i} rel="alternate" href={toAbsoluteUrl(l.url)} hrefLang={hreflang} />
          })}

          <meta property="og:site_name" content="https://miro.com/" />
          <meta property="og:type" content="website" />
          <meta property="og:determiner" content="a" />

          <meta property="og:title" content={page.fields.openGraphTitle || page.fields.title} />
          <meta
            property="og:description"
            content={page.fields.openGraphDescription || page.fields.description}
          />
          <meta
            property="og:image"
            content={
              page.fields.openGraphImage?.fields?.file
                ? 'https:' + page.fields.openGraphImage.fields.file.url
                : defaultOpenGraphImage
            }
          />

          {page.fields.noIndex && <meta name="robots" content="noindex" />}

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:url" content="https://miro.com/" />
          <meta name="twitter:title" content={page.fields.openGraphTitle || page.fields.title} />
          <meta
            name="twitter:description"
            content={page.fields.openGraphDescription || page.fields.description}
          />
          <meta
            name="twitter:image"
            content={
              page.fields.openGraphImage?.fields?.file
                ? 'https:' + page.fields.openGraphImage.fields.file.url
                : defaultOpenGraphImage
            }
          />
        </Head>

        {headerComponent}

        <Container>
          <main>{layout}</main>
        </Container>
        {footerComponent}

        {page.fields.isHomePage && <DynamicPopupBanner />}

        {page.fields.customScripts &&
          page.fields.customScripts.reduce((acc, item) => {
            if (item.fields?.file?.url) {
              acc.push(
                <Script
                  key={item.fields.file.url}
                  src={item.fields.file.url}
                  strategy="lazyOnload"
                />,
              )
            }

            return acc
          }, [])}

        {preview && (
          <PreviewMode
            stopPreviewPath={`${
              process.env.NEXT_PUBLIC_API_BASE_PATH ? process.env.NEXT_PUBLIC_API_BASE_PATH : '/api'
            }/stop-preview/`}
          />
        )}
      </>
    </StyleSheetManager>
  )
}

export default Page
